<template>
  <div>
    <el-collapse v-model="activeEventTypes" rounded>
      <el-collapse-item
        v-for="group in groupedNotifications"
        :key="group.eventType"
        :name="group.eventType"
        class="table"
      >
        <template #title>
          <span class="collapse-title">{{ group.eventType }}</span>
        </template>

        <!-- Table header -->
        <el-card
          v-if="!isMobile"
          shadow="never"
          class="table-header"
          body-style="padding: 4px;"
        >
          <el-row :gutter="12" align="middle">
            <el-col :span="6">Name</el-col>
            <el-col :span="3">Transport</el-col>
            <el-col :span="3">Status</el-col>
            <el-col :span="6">Message</el-col>
            <el-col :span="6" class="actions">Actions</el-col>
          </el-row>
        </el-card>

        <!-- Items -->
        <el-card
          v-for="item in group.items"
          :key="item.id"
          shadow="hover"
          body-style="padding: 8px;"
        >
          <el-row
            :gutter="isMobile ? 8 : 12"
            align="middle"
            :class="{ 'mobile-card': isMobile }"
          >
            <!-- Name -->
            <el-col :span="isMobile ? 24 : 6">
              <b>Name:</b> {{ item.attributes.name || '—' }}
            </el-col>

            <!-- Transport with icon -->
            <el-col :span="isMobile ? 12 : 3">
              <el-tag
                :type="transportType(item.attributes.transport)"
                effect="plain"
                class="tag"
              >
                <font-awesome-icon
                  :icon="transportIcon(item.attributes.transport)"
                  class="tag-icon transport-icon"
                />
                {{ transportLabel(item.attributes.transport) }}
              </el-tag>
            </el-col>

            <!-- Status -->
            <el-col :span="isMobile ? 12 : 3">
              <el-tag
                :type="item.attributes.active ? 'success' : 'danger'"
                effect="plain"
                class="tag"
              >
                <font-awesome-icon
                  :icon="item.attributes.active ? 'check' : 'ban'"
                  class="tag-icon"
                />
                {{ item.attributes.active ? 'Active' : 'Disabled' }}
              </el-tag>
            </el-col>

            <!-- Transport message -->
            <el-col :span="isMobile ? 12 : 6">
              <b v-if="isMobile">Message:</b>
              {{
                item.attributes.message?.trim() ? item.attributes.message : '—'
              }}
            </el-col>

            <!-- Actions -->
            <el-col :span="isMobile ? 12 : 6" class="actions">
              <AutomationActions :automation="item.attributes" />
            </el-col>
          </el-row>
        </el-card>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useAutomationsStore } from '@/store/modules/automations'
import AutomationActions from './AutomationActions.vue'
import {
  transportType,
  transportIcon,
  transportLabel
} from '@/utils/automations'
import useResize from '@/hooks/useResize'

export default defineComponent({
  name: 'AutomationsTable',
  components: { AutomationActions },
  setup() {
    const automationsStore = useAutomationsStore()
    const { isMobile } = useResize()

    const groupedNotifications = computed(() => {
      return automationsStore.events.map((event) => ({
        eventType: event.label,
        value: event.value,
        items: automationsStore.getItemsByType(event.value)
      }))
    })

    const activeEventTypes = ref<string[]>([])

    watch(
      groupedNotifications,
      (newVal) => {
        if (newVal.length > 0 && activeEventTypes.value.length === 0) {
          activeEventTypes.value = [newVal[0].eventType]
        }
      },
      { immediate: true }
    )

    return {
      groupedNotifications,
      activeEventTypes,
      transportIcon,
      transportType,
      transportLabel,
      isMobile
    }
  }
})
</script>

<style lang="scss" scoped>
.table {
  > * {
    padding: 0 10px;
  }

  .el-card {
    margin-bottom: 8px;
  }

  .el-card:last-child {
    margin-bottom: 0;
  }
}

.collapse-title {
  font-weight: bold;
}

.table-header {
  background-color: var(--app-bg-color);
  font-weight: bold;
}

.actions {
  text-align: right;
}

.tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  text-align: center;
  .tag-icon {
    margin-right: 6px;
    &.transport-icon {
      font-size: 16px;
    }
  }
}

.mobile-card {
  font-size: 14px;
  line-height: 1.4;
}

::v-deep(.el-collapse-item__header) {
  display: flex;
  flex-direction: row-reverse;
  justify-content: left;
  align-items: center;
}
::v-deep(.el-collapse-item__arrow) {
  margin-left: 0 !important;
  margin-right: 8px;
}
</style>
